// Higher-order reducer for commission-service list slices.
//
// Every list endpoint in the service has the same shape, so every list slice
// gets the same state and the same reducer rather than six hand-written copies:
//
//   { byGuid, guids, pagination: { page, size, total }, loading, error }
//
// Detail fetches merge into `byGuid`, which is what lets a transactions page
// resolve agent names from already-loaded agents instead of refetching.
//
// ES5 only: no optional chaining, no object spread.

export var DEFAULT_PAGE = 1;
export var DEFAULT_SIZE = 10;

export function initialListState(overrides) {
  var state = {
    byGuid: {},
    guids: [],
    pagination: { page: DEFAULT_PAGE, size: DEFAULT_SIZE, total: 0 },
    loading: false,
    error: null
  };
  return overrides ? Object.assign(state, overrides) : state;
}

// List responses come back as { data, total, page, size, totalPages }, but some
// endpoints return a bare array. This normalises both, and is the one place
// that knows the response meta exists — the old sagas discarded it, which is
// why pagination had to guess `hasMore` from the row count.
export function readListResponse(payload, fallback) {
  var defaults = fallback || {};
  var page = defaults.page || DEFAULT_PAGE;
  var size = defaults.size || DEFAULT_SIZE;

  if (!payload) {
    return { rows: [], page: page, size: size, total: 0 };
  }

  if (Array.isArray(payload)) {
    return { rows: payload, page: page, size: size, total: payload.length };
  }

  var rows = Array.isArray(payload.data) ? payload.data : [];
  return {
    rows: rows,
    page: Number(payload.page) || page,
    size: Number(payload.size) || size,
    // Fall back to the row count so pagination still works if the service
    // omits `total`, rather than reporting zero results for a full page.
    total: payload.total === undefined || payload.total === null
      ? rows.length
      : Number(payload.total)
  };
}

function indexRows(rows, idField) {
  var byGuid = {};
  var guids = [];
  for (var i = 0; i < rows.length; i += 1) {
    var row = rows[i];
    if (!row) continue;
    var id = row[idField];
    if (id === undefined || id === null) continue;
    byGuid[id] = row;
    if (guids.indexOf(id) === -1) guids.push(id);
  }
  return { byGuid: byGuid, guids: guids };
}

// Creates a list reducer.
//
//   types.request / types.success / types.failure  the list triad
//   types.detailSuccess   optional: merges a single record into byGuid
//   types.mergeMany       optional: merges an array of records into byGuid
//                         without touching `guids` (bulk name resolution)
//   types.reset           optional: back to initial state
//
// options.idField  defaults to 'guid'
// options.keepRowsWhileLoading  when true, the previous page stays visible
//                               during a refetch instead of blanking the table
export function createListReducer(types, options) {
  var opts = options || {};
  var idField = opts.idField || 'guid';
  var keepRows = opts.keepRowsWhileLoading === true;
  var initial = initialListState(opts.initial);

  return function listReducer(state, action) {
    if (state === undefined) state = initial;
    if (!action || !action.type) return state;

    switch (action.type) {
      case types.request: {
        // The requested page/size is echoed into state immediately so the
        // pagination control reflects the pending request.
        var requested = action.payload || {};
        var pagination = Object.assign({}, state.pagination);
        if (requested.page !== undefined) {
          pagination.page = Number(requested.page) || DEFAULT_PAGE;
        }
        if (requested.size !== undefined) {
          pagination.size = Number(requested.size) || DEFAULT_SIZE;
        }
        return Object.assign({}, state, {
          loading: true,
          error: null,
          pagination: pagination,
          byGuid: keepRows ? state.byGuid : {},
          guids: keepRows ? state.guids : []
        });
      }

      case types.success: {
        var read = readListResponse(action.payload, state.pagination);
        var indexed = indexRows(read.rows, idField);
        return Object.assign({}, state, {
          loading: false,
          error: null,
          // Merge rather than replace so records fetched individually earlier
          // are not evicted by a page that does not include them.
          byGuid: Object.assign({}, state.byGuid, indexed.byGuid),
          guids: indexed.guids,
          pagination: { page: read.page, size: read.size, total: read.total }
        });
      }

      case types.failure:
        return Object.assign({}, state, {
          loading: false,
          error: action.payload || { message: 'Request failed' }
        });

      default:
        break;
    }

    if (types.detailSuccess && action.type === types.detailSuccess) {
      var record = action.payload;
      if (!record || record[idField] === undefined) return state;
      var one = {};
      one[record[idField]] = record;
      return Object.assign({}, state, {
        byGuid: Object.assign({}, state.byGuid, one)
      });
    }

    if (types.mergeMany && action.type === types.mergeMany) {
      var many = action.payload;
      var rows = Array.isArray(many)
        ? many
        : readListResponse(many, state.pagination).rows;
      var merged = indexRows(rows, idField);
      return Object.assign({}, state, {
        byGuid: Object.assign({}, state.byGuid, merged.byGuid)
      });
    }

    if (types.reset && action.type === types.reset) {
      return initialListState(opts.initial);
    }

    return state;
  };
}

// Combines several list reducers (and any extra plain reducers) into one slice
// reducer, so a feature with three lists does not need three top-level slices.
export function combineSliceReducers(map) {
  var keys = Object.keys(map);
  return function sliceReducer(state, action) {
    if (state === undefined) state = {};
    var next = state;
    var changed = false;
    for (var i = 0; i < keys.length; i += 1) {
      var key = keys[i];
      var before = state[key];
      var after = map[key](before, action);
      if (after !== before) {
        if (!changed) {
          next = Object.assign({}, state);
          changed = true;
        }
        next[key] = after;
      } else if (!changed && before === undefined) {
        next = Object.assign({}, state);
        changed = true;
        next[key] = after;
      }
    }
    return next;
  };
}

// ─── Write-operation state ───────────────────────────────────────────────────

// Per-operation triad state, so there is no single global spinner flag.
// `submitting` is the double-click guard for anything that moves money: it is
// set on REQUEST and cleared only on SUCCESS or FAILURE, and the button reads
// it. Server state remains the final truth — this only stops a second dispatch.
export function initialWriteState() {
  return { submitting: false, success: false, error: null, result: null };
}

export function createWriteReducer(types) {
  return function writeReducer(state, action) {
    if (state === undefined) state = initialWriteState();
    if (!action || !action.type) return state;

    switch (action.type) {
      case types.request:
        return { submitting: true, success: false, error: null, result: null };
      case types.success:
        return {
          submitting: false,
          success: true,
          error: null,
          result: action.payload || null
        };
      case types.failure:
        return {
          submitting: false,
          success: false,
          error: action.payload || { message: 'Request failed' },
          result: null
        };
      default:
        if (types.reset && action.type === types.reset) {
          return initialWriteState();
        }
        return state;
    }
  };
}
